import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { StoreExtraDependencies } from "../index";
import type { RootState } from "../index";

/**
 * Password Reset State Status
 */
export type PasswordResetStatus =
  | "idle"
  | "requesting"
  | "resetting"
  | "success";

/**
 * Password Reset State Interface
 */
export interface PasswordResetState {
  status: PasswordResetStatus;
  email: string;
  error: string | null;
  resendCooldown: number; // Seconds remaining before resend is allowed
}

/**
 * Initial State
 */
const initialState: PasswordResetState = {
  status: "idle",
  email: "",
  error: null,
  resendCooldown: 0,
};

/**
 * Async Thunk: Request Password Reset
 *
 * Sends password reset code to user's email.
 * Uses injected authGateway from store dependencies.
 */
export const forgotPasswordAsync = createAsyncThunk<
  void,
  string, // email parameter
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "passwordReset/forgotPassword",
  async (email, { extra, rejectWithValue }) => {
    try {
      await extra.authGateway.forgotPassword(email);
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("passwordReset.errors.unknownError");
    }
  }
);

/**
 * Async Thunk: Reset Password
 *
 * Submits verification code and new password to complete password reset.
 * Uses injected authGateway from store dependencies.
 */
export const resetPasswordAsync = createAsyncThunk<
  void,
  { email: string; code: string; newPassword: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "passwordReset/resetPassword",
  async ({ email, code, newPassword }, { extra, rejectWithValue }) => {
    try {
      await extra.authGateway.forgotPasswordSubmit(email, code, newPassword);
    } catch (error) {
      if (error instanceof Error) {
        // Gateway already throws i18n keys, pass through directly
        return rejectWithValue(error.message);
      }
      return rejectWithValue("passwordReset.errors.unknownError");
    }
  }
);

/**
 * Async Thunk: Resend Password Reset Code
 *
 * Resends password reset code to user's email.
 * Uses injected authGateway from store dependencies.
 */
export const resendCodeAsync = createAsyncThunk<
  void,
  string, // email parameter
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "passwordReset/resendCode",
  async (email, { extra, rejectWithValue }) => {
    try {
      await extra.authGateway.resendPasswordResetCode(email);
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("passwordReset.errors.unknownError");
    }
  }
);

/**
 * Password Reset Slice
 */
const passwordResetSlice = createSlice({
  name: "passwordReset",
  initialState,
  reducers: {
    /**
     * Reset password reset state to initial values
     */
    resetPasswordResetState: () => initialState,

    /**
     * Clear error message
     */
    clearPasswordResetError: (state) => {
      state.error = null;
    },

    /**
     * Decrement resend cooldown timer
     */
    decrementResendCooldown: (state) => {
      if (state.resendCooldown > 0) {
        state.resendCooldown -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    // Forgot Password (Request Reset)
    builder
      .addCase(forgotPasswordAsync.pending, (state, action) => {
        state.status = "requesting";
        state.error = null;
        state.email = action.meta.arg; // Store email for reset step
      })
      .addCase(forgotPasswordAsync.fulfilled, (state) => {
        state.status = "idle"; // Ready for reset step
        state.error = null;
        state.resendCooldown = 60; // 60-second cooldown
      })
      .addCase(forgotPasswordAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload || "passwordReset.errors.unknownError";
      });

    // Reset Password (Submit Code + New Password)
    builder
      .addCase(resetPasswordAsync.pending, (state) => {
        state.status = "resetting";
        state.error = null;
      })
      .addCase(resetPasswordAsync.fulfilled, (state) => {
        state.status = "success";
        state.error = null;
        // Don't clear email yet - it will be cleared when user navigates
        // or when ForgotPasswordPage mounts for next reset flow
        // Cooldown can be reset since we're done with this flow
        state.resendCooldown = 0;
      })
      .addCase(resetPasswordAsync.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.payload || "passwordReset.errors.unknownError";
      });

    // Resend Code
    builder
      .addCase(resendCodeAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(resendCodeAsync.fulfilled, (state) => {
        state.error = null;
        state.resendCooldown = 60; // Reset cooldown
      })
      .addCase(resendCodeAsync.rejected, (state, action) => {
        state.error = action.payload || "passwordReset.errors.unknownError";
      });
  },
});

/**
 * Actions
 */
export const {
  resetPasswordResetState,
  clearPasswordResetError,
  decrementResendCooldown,
} = passwordResetSlice.actions;

/**
 * Selectors
 */
export const selectPasswordReset = (state: RootState) => state.passwordReset;
export const selectPasswordResetStatus = (state: RootState) =>
  state.passwordReset.status;
export const selectPasswordResetEmail = (state: RootState) =>
  state.passwordReset.email;
export const selectPasswordResetError = (state: RootState) =>
  state.passwordReset.error;
export const selectResendCooldown = (state: RootState) =>
  state.passwordReset.resendCooldown;

/**
 * Reducer
 */
export default passwordResetSlice.reducer;
